import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  signal,
  viewChild,
} from "@angular/core";
import { QUANTITE_PRODUITE_PERIODE_DATA } from "../../models/data.model";
import { SharedModule } from "../../../../shared/shared.module";
import { WidgetHelperService } from "../../services/widget-helper.service";
import { Chart } from "chart.js";
import { Widget } from "../../models/dashboard.model";
import { EntityMetadata } from "../../../../shared/store/store-types.model";
import { WidgetTitleComponent } from "../../../../shared/components/widget-title/widget-title.component";
import { WidgetPeriodFiltersComponent } from "../../../../shared/components/widget-period-filters/widget-period-filters.component";
import { SelectAutocompleteComponent } from "../../../../shared/modules/select-autocomplete/select-autocomplete.component";

@Component({
  selector: "vl-widget-quantite-produite-periode",
  standalone: true,
  imports: [
    SharedModule,
    WidgetTitleComponent,
    WidgetPeriodFiltersComponent,
    SelectAutocompleteComponent,
  ],
  host: { class: "widget-component" },
  templateUrl: "./widget-quantite-produite-periode.component.html",
  styleUrl: "./widget-quantite-produite-periode.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WidgetQuantiteProduitePeriodeComponent {
  data = input<QUANTITE_PRODUITE_PERIODE_DATA>();
  metadata = input<EntityMetadata>();
  widget = input<Widget>();
  elementRef = inject(ElementRef);

  period = signal<string>("m");
  comparison = signal<string | null>(null);
  timeScale = signal<string>("YTD");
  selectedLigne = signal<string | null>(null);

  canvasRef = viewChild<ElementRef>("qteProduite");

  chart: Chart;

  lignes = computed(() => {
    const d = this.data();
    if (!d?.data) return [];
    return [...new Set(d.data.map((item) => item.ligneDenomination))].sort();
  });

  ligneElements = computed(() => {
    const items = this.lignes();
    return [
      { name: "Toutes", value: null as string | null },
      ...items.map((l) => ({ name: l, value: l as string | null })),
    ];
  });

  constructor() {
    effect(
      () => {
        const data = this.data();
        const canvas = this.canvasRef();
        this.period();
        this.comparison();
        this.timeScale();
        this.selectedLigne();

        if (data && canvas) {
          this.buildChart();
        }
      },
      { allowSignalWrites: true },
    );
  }

  onLigneChange(value: string | null) {
    this.selectedLigne.set(value);
  }

  buildChart() {
    if (this.data()?.data?.length > 0) {
      const result = WidgetHelperService.getQuantiteProduiteComparisonChart(
        this.data().data,
        {
          timeScale: this.timeScale(),
          period: this.period(),
          comparison: this.comparison(),
          selectedLigne: this.selectedLigne(),
        },
      );

      const bgAlt = getComputedStyle(
        this.elementRef.nativeElement,
      ).getPropertyValue("--color-on-background-alt");

      const hasComparison =
        this.comparison() && result.datasets.length > 1;

      const isStacked = result.datasets.some((ds) => ds.stack);

      const compLabels = result.comparisonLabels;

      const canvas = this.canvasRef().nativeElement;
      const ctx = canvas.getContext("2d");

      if (this.chart) {
        this.chart.destroy();
      }

      this.chart = new Chart(ctx, {
        type: "bar",
        data: { datasets: result.datasets, labels: result.labels },
        options: {
          layout: { padding: { top: 0, bottom: 0, left: 0, right: 0 } },
          plugins: {
            legend: {
              display: result.datasets.length > 1,
              position: "bottom",
              labels: {
                color: bgAlt,
                boxWidth: 10,
                padding: 6,
                font: { size: 10 },
              },
            },
            tooltip: {
              callbacks: {
                title: (items) => {
                  if (!items.length) return "";
                  const idx = items[0].dataIndex;
                  const currentLabel = result.labels[idx] ?? "";
                  if (hasComparison && compLabels[idx]) {
                    return `${currentLabel} vs ${compLabels[idx]}`;
                  }
                  return currentLabel;
                },
                label: (context) => {
                  const value = context.parsed.y ?? 0;
                  const datasetIdx = context.datasetIndex;
                  const idx = context.dataIndex;
                  let periodLabel = "";
                  if (hasComparison && datasetIdx === 1 && compLabels[idx]) {
                    periodLabel = ` (${compLabels[idx]})`;
                  }
                  return `${context.dataset.label}${periodLabel}: ${WidgetHelperService.formatNumeric(value)}`;
                },
              },
            },
          },
          responsive: true,
          maintainAspectRatio: false,
          scales: {
            x: {
              stacked: isStacked,
              ticks: {
                color: bgAlt,
                maxRotation: 45,
                font: { size: 10 },
              },
            },
            y: {
              stacked: isStacked,
              ticks: {
                color: bgAlt,
                font: { size: 10 },
                callback: (value) =>
                  WidgetHelperService.formatNumeric(+value),
              },
            },
          },
        },
      });
    }
  }
}
